import { useEffect, useRef, useState } from 'react';
import { socket } from '../socket';
import { getSanitizedYouTubeWatchUrl, getSafeAvatarUrl } from '../utils/validation';
import type { DisplayQueueItem, Moment, QueueMessage } from '../types';
import { useI18n } from '../i18n/useI18n';

interface Props {
  msg?: QueueMessage;
  moment?: Moment;
  idx: number;
  queue: DisplayQueueItem[];
  isHost: boolean;
  isOwner: boolean;
  roomId: string | undefined;
  isNowPlaying?: boolean;
  draggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: () => void;
  onDragOver?: () => void;
  onDrop?: () => void;
  onDragEnd?: () => void;
  onRemoveMoment?: () => void;
  onMoveMoment?: (dir: -1 | 1) => void;
  onRemove?: () => void;
  disableRemove?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  canDuet?: boolean;
  onDuet?: () => void;
  partnerNames?: string[];
  highlight?: boolean;
  waitTimeLabel?: string;
}

export default function QueueItem({
  msg,
  moment,
  idx,
  queue,
  isHost,
  isOwner,
  roomId,
  isNowPlaying,
  draggable,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onRemoveMoment,
  onMoveMoment,
  onRemove,
  disableRemove,
  isFavorite,
  onToggleFavorite,
  canDuet,
  onDuet,
  partnerNames,
  highlight,
  waitTimeLabel,
}: Props) {
  const { t } = useI18n();
  const isMoment = !!moment;
  const id = msg?.id || moment?.id || `slot-${idx}`;
  const displayNames = partnerNames && partnerNames.length > 0
    ? [msg?.userName, ...partnerNames].filter(Boolean).join(' + ')
    : msg?.userName;
  return (
    <div
      data-message-id={id}
      className={`queue-item ${idx === 0 ? 'active' : ''} ${highlight ? 'queue-item--mine' : ''} ${isDragging ? 'queue-item--dragging' : ''} ${isDragOver ? 'queue-item--drag-over' : ''} ${isMoment ? 'queue-item--moment' : ''} ${draggable ? 'queue-item--draggable' : ''}`}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
        onDragStart?.();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver?.();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.();
      }}
      onDragEnd={onDragEnd}
    >
      <div className="queue-item__start">
        {isHost && <div className="queue-number">{idx + 1}</div>}
        {draggable && (
          <span className="queue-drag-handle" aria-hidden="true">
            ⋮⋮
          </span>
        )}
        {isMoment ? (
          <div className="queue-item__moment-icon">
            {moment?.type === 'end' ? '⏹' : moment?.type === 'intro' ? '✨' : '⏸'}
          </div>
        ) : (
          msg?.metadata?.thumbnail && getSafeAvatarUrl(msg.metadata.thumbnail) && (
            <img
              src={getSafeAvatarUrl(msg.metadata.thumbnail)}
              alt=""
              className="queue-item__thumb"
            />
          )
        )}
        <div>
          <div className="queue-item__title">
            {isMoment ? moment?.title : msg?.content}
            {waitTimeLabel && <span className="queue-item__wait-chip">{waitTimeLabel}</span>}
          </div>
          <div className="queue-item__meta">
            {isMoment ? (
              <span>{moment?.description || t('singer.queue.momentFallback')}</span>
            ) : (
              <span>{t('singer.queue.addedBy', { name: displayNames ?? '' })}</span>
            )}
          </div>
        </div>
      </div>
      <div className="queue-item__actions">
        {isHost && !isMoment && idx > 0 && (
          <>
            <button
              className="btn-secondary queue-item__reorder"
              onClick={() => {
                if (idx === 0) return;
                const newOrder = queue.filter((m) => m.kind === 'song').map((m) => m.data.id);
                const songIdx = queue.slice(0, idx).filter((m) => m.kind === 'song').length;
                if (songIdx === 0) return;
                [newOrder[songIdx], newOrder[songIdx - 1]] = [newOrder[songIdx - 1], newOrder[songIdx]];
                socket.emit('reorder_queue', { roomId, newOrderIds: newOrder });
              }}
              aria-label={t('singer.queue.moveUp')}
            >
              ▲
            </button>
            <button
              className={`btn-secondary queue-item__reorder ${(() => {
                const songIdx = queue.slice(0, idx).filter((m) => m.kind === 'song').length;
                const songCount = queue.filter((m) => m.kind === 'song').length;
                return songIdx === songCount - 1 ? 'queue-item__reorder--disabled' : '';
              })()}`}
              onClick={() => {
                const songCount = queue.filter((m) => m.kind === 'song').length;
                const songIdx = queue.slice(0, idx).filter((m) => m.kind === 'song').length;
                if (songIdx === songCount - 1) return;
                const newOrder = queue.filter((m) => m.kind === 'song').map((m) => m.data.id);
                [newOrder[songIdx], newOrder[songIdx + 1]] = [newOrder[songIdx + 1], newOrder[songIdx]];
                socket.emit('reorder_queue', { roomId, newOrderIds: newOrder });
              }}
              disabled={(() => {
                const songIdx = queue.slice(0, idx).filter((m) => m.kind === 'song').length;
                const songCount = queue.filter((m) => m.kind === 'song').length;
                return songIdx === songCount - 1;
              })()}
              aria-label={t('singer.queue.moveDown')}
            >
              ▼
            </button>
            {msg?.metadata?.videoId && getSanitizedYouTubeWatchUrl(msg.metadata.videoId) && (
              <a
                href={getSanitizedYouTubeWatchUrl(msg.metadata.videoId)!}
                target="_blank"
                rel="noopener noreferrer"
                className="queue-item__youtube-link"
              >
                <button
                  className="queue-action-btn queue-action-btn--youtube"
                  aria-label={t('singer.queue.openInYouTube')}
                  title={t('singer.queue.openInYouTube')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </button>
              </a>
            )}
          </>
        )}
        {isMoment && isHost && (
          <>
            <button
              className="btn-secondary queue-item__reorder"
              onClick={() => onMoveMoment?.(-1)}
              disabled={idx === 0}
              aria-label={t('singer.queue.moveUp')}
            >
              ▲
            </button>
            <button
              className="btn-secondary queue-item__reorder"
              onClick={() => onMoveMoment?.(1)}
              disabled={idx === queue.length - 1}
              aria-label={t('singer.queue.moveDown')}
            >
              ▼
            </button>
            <button
              className="queue-action-btn queue-action-btn--danger"
              onClick={onRemoveMoment}
              aria-label={t('singer.queue.removeMoment')}
              title={t('singer.queue.removeMoment')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 2 0 0 1-2-2V6m3 0V4a2 2 2 0 0 1 2-2h4a2 2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          </>
        )}
        {onToggleFavorite && !isMoment && (
          <button
            className={`queue-action-btn queue-action-btn--star ${isFavorite ? 'queue-action-btn--star-active' : ''}`}
            onClick={onToggleFavorite}
            aria-label={isFavorite ? t('singer.queue.removeFromFavorites') : t('singer.queue.addToFavorites')}
            title={isFavorite ? t('singer.queue.saved') : t('singer.queue.saveSong')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        )}
        {canDuet && !isMoment && !isNowPlaying && idx > 1 && (
          <button
            className="queue-action-btn queue-action-btn--duet"
            onClick={onDuet}
            aria-label={t('duet.invite')}
            title={t('duet.invite')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
          </button>
        )}
        {(isHost || isOwner) && !isMoment && !isNowPlaying && !disableRemove && (
          <RemoveButton messageId={msg!.id} roomId={roomId} onRemove={onRemove} />
        )}
      </div>
    </div>
  );
}

function RemoveButton({ messageId, roomId, onRemove }: { messageId: string; roomId: string | undefined; onRemove?: () => void }) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const confirmTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(confirmTimerRef.current), []);

  const handleClick = () => {
    if (!roomId) return;
    // Dois toques para remover: o primeiro arma a confirmação por ~3s — no
    // mobile um toque acidental no ✕ apagava a música imediatamente.
    if (!confirming) {
      setConfirming(true);
      confirmTimerRef.current = window.setTimeout(() => setConfirming(false), 3000);
      return;
    }
    window.clearTimeout(confirmTimerRef.current);
    setConfirming(false);
    onRemove ? onRemove() : socket.emit('delete_own_message', messageId);
  };

  return (
    <button
      className={`queue-action-btn queue-action-btn--danger${confirming ? ' queue-action-btn--confirm' : ''}`}
      onClick={handleClick}
      aria-label={confirming ? t('common.confirmRemove') : t('common.remove')}
      title={confirming ? t('common.confirmRemove') : t('common.remove')}
    >
      {confirming ? (
        <span className="queue-action-confirm-label">{t('common.confirmRemove')}</span>
      ) : (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      )}
    </button>
  );
}
